/**
 * /habilitacion — Cockpit del set de pruebas DIAN (habilitación PT).
 *
 * Composición oficial del set (md/985 §5.1, asignada por DIAN en MUISCA):
 * 30 facturas + 10 notas débito + 10 notas crédito TRANSMITIDAS a
 * vpfe-hab vía SendTestSetAsync. Umbral real de aprobación: 1 factura
 * ACEPTADA (el resto solo debe transmitirse).
 *
 * El cockpit muestra progreso por tipo, la tabla de documentos del set
 * (track_id + CUFE copiables), y permite crear el lote inicial, crear
 * NC/ND y transmitir los pendientes en batch. El backend identifica los
 * documentos del set con el marker en notes; el GET status devuelve los
 * agregados + documents + transmitter_ready server-side.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Copy,
  Loader2,
  PlayCircle,
  Send,
  ShieldAlert,
  Target,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  habilitacionApi,
  invoiceApi,
  type HabilitacionDocument,
  type InvoiceDocType,
  type InvoiceStatus,
} from '@/lib/api'
import { DIAN_HABILITACION_SET, ROUTES } from '@/lib/constants'

// Poll cada 15s mientras haya documentos en tránsito (submitting);
// apagado cuando todo está en estado terminal — evita tráfico inútil.
const POLL_INTERVAL_MS = 15_000

// Pausa entre envíos secuenciales del batch para no saturar vpfe-hab
// (throttle suave; el SIE de habilitación degrada con ráfagas).
const BATCH_SUBMIT_THROTTLE_MS = 500

const DOC_TYPE_LABELS: Record<InvoiceDocType, string> = {
  invoice: 'Factura',
  credit_note: 'Nota crédito',
  debit_note: 'Nota débito',
}

const STATUS_BADGES: Record<InvoiceStatus, { bg: string; fg: string; label: string }> = {
  draft: { bg: 'bg-bg-elevated', fg: 'text-fg-secondary', label: 'Borrador' },
  submitting: { bg: 'bg-info/15', fg: 'text-info', label: 'En tránsito DIAN' },
  accepted: { bg: 'bg-success-tint', fg: 'text-success-fg', label: 'Aceptada' },
  rejected: { bg: 'bg-error-tint', fg: 'text-error-fg', label: 'Rechazada' },
  failed: { bg: 'bg-warning-tint', fg: 'text-warning-fg', label: 'Falló' },
}

// Un documento es transmisible si está en draft (primer envío) o failed
// (re-transmisión) — mismos estados que acepta el orquestador backend.
const isPendingTransmit = (d: HabilitacionDocument) =>
  d.status === 'draft' || d.status === 'failed'

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

function errMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } }
    message?: string
  }
  return e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? 'error'
}

function fmtDateTime(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CO', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function copyText(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(`${label} copiado al portapapeles`)
  } catch {
    toast.error(`No se pudo copiar el ${label}`)
  }
}

export function HabilitacionPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const qc = useQueryClient()

  // Progreso del batch "Transmitir pendientes" (null = no corriendo).
  const [batch, setBatch] = useState<{ done: number; total: number } | null>(null)

  const status = useQuery({
    queryKey: ['habilitacion-status', activeOrgId],
    queryFn: () => habilitacionApi.status(activeOrgId!),
    enabled: !!activeOrgId,
    refetchInterval: (query) => {
      const d = query.state.data
      if (!d) return false
      const inTransit = d.documents?.some((doc) => doc.status === 'submitting')
      return inTransit ? POLL_INTERVAL_MS : false
    },
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['habilitacion-status', activeOrgId] })

  // ─── Mutations: creación de documentos del set ────────────────────

  const createInvoices = useMutation({
    mutationFn: () => habilitacionApi.createInitialInvoices(activeOrgId!),
    onSuccess: (r) => {
      toast.success(`${r.created} facturas del set creadas (borradores)`)
      invalidate()
    },
    onError: (err) => toast.error(`No se pudo crear el lote: ${errMessage(err)}`),
  })

  const createNC = useMutation({
    mutationFn: () => habilitacionApi.createCreditNote(activeOrgId!),
    onSuccess: () => {
      toast.success('Nota crédito creada (borrador)')
      invalidate()
    },
    onError: (err) => toast.error(`No se pudo crear la NC: ${errMessage(err)}`),
  })

  const createND = useMutation({
    mutationFn: () => habilitacionApi.createDebitNote(activeOrgId!),
    onSuccess: () => {
      toast.success('Nota débito creada (borrador)')
      invalidate()
    },
    onError: (err) => toast.error(`No se pudo crear la ND: ${errMessage(err)}`),
  })

  // ─── Mutations: transmisión a DIAN ────────────────────────────────

  // Transmisión de UN documento (botón por fila: Transmitir / Re-transmitir).
  const submitOne = useMutation({
    mutationFn: (docId: string) => invoiceApi.submitToDIAN(activeOrgId!, docId),
    onSuccess: (r) => {
      if (r.async) {
        toast.info('Transmitido a DIAN (async). El estado definitivo llega por polling.')
      } else if (r.accepted) {
        toast.success('Documento ACEPTADO por DIAN')
      } else {
        toast.error('Documento RECHAZADO por DIAN — revisa el detalle')
      }
      invalidate()
    },
    onError: (err) => {
      if (httpStatus(err) === 503) {
        // Incluye la causa server-side (transmitter_message del status)
        // para que el toast sea accionable, no solo el HTTP crudo.
        const cause =
          !status.data?.transmitter_ready && status.data?.transmitter_message
            ? `: ${status.data.transmitter_message}`
            : ' — ver el banner de bloqueo'
        toast.error(`Transmisor DIAN no disponible${cause}`)
      } else {
        toast.error(`Transmisión falló: ${errMessage(err)}`)
      }
      invalidate() // refresca transmitter_ready + status del doc
    },
  })

  // Transmisión en batch de todos los pendientes (draft + failed),
  // secuencial para no saturar vpfe-hab. Si el transmisor responde 503
  // se aborta de inmediato (los demás fallarían igual).
  const transmitPending = useMutation({
    mutationFn: async (pending: HabilitacionDocument[]) => {
      const result = { accepted: 0, async: 0, rejected: 0, failed: 0, blocked: false }
      setBatch({ done: 0, total: pending.length })
      for (let i = 0; i < pending.length; i++) {
        try {
          const r = await invoiceApi.submitToDIAN(activeOrgId!, pending[i].id)
          if (r.async) result.async++
          else if (r.accepted) result.accepted++
          else result.rejected++
        } catch (err) {
          if (httpStatus(err) === 503) {
            result.blocked = true
            break
          }
          result.failed++
        }
        setBatch({ done: i + 1, total: pending.length })
        // Throttle entre envíos para no saturar vpfe-hab (no aplica
        // después del último documento).
        if (i < pending.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, BATCH_SUBMIT_THROTTLE_MS))
        }
      }
      return result
    },
    onSuccess: (r) => {
      if (r.blocked) {
        const cause =
          !status.data?.transmitter_ready && status.data?.transmitter_message
            ? ` — ${status.data.transmitter_message}`
            : ''
        toast.error(`Batch abortado: el transmisor DIAN no está disponible (503)${cause}`)
        return
      }
      const parts: string[] = []
      if (r.accepted) parts.push(`${r.accepted} aceptados`)
      if (r.async) parts.push(`${r.async} en tránsito (async)`)
      if (r.rejected) parts.push(`${r.rejected} rechazados por DIAN`)
      if (r.failed) parts.push(`${r.failed} con error técnico`)
      const summary = parts.length ? parts.join(', ') : 'sin documentos procesados'
      if (r.rejected || r.failed) toast.warning(`Batch terminado: ${summary}`)
      else toast.success(`Batch terminado: ${summary}`)
    },
    onError: (err) => toast.error(`Batch falló: ${errMessage(err)}`),
    onSettled: () => {
      setBatch(null)
      invalidate()
    },
  })

  const transmitting = submitOne.isPending || transmitPending.isPending

  // ─── Estados de carga / error de la página ────────────────────────

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  if (status.isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando estado del set...
        </div>
      </AppShell>
    )
  }

  if (status.isError || !status.data) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Error: {(status.error as Error)?.message ?? 'no se pudo cargar el estado'}
        </div>
      </AppShell>
    )
  }

  const s = status.data
  const docs = s.documents ?? []
  const pending = docs.filter(isPendingTransmit)
  const thresholdMet = s.invoices_accepted >= DIAN_HABILITACION_SET.MIN_ACCEPTED_INVOICES

  // Progreso NC/ND derivado de la tabla de documentos.
  const ncDocs = docs.filter((d) => d.doc_type === 'credit_note')
  const ndDocs = docs.filter((d) => d.doc_type === 'debit_note')

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-bold">Habilitación DIAN — cockpit del set de pruebas</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Composición oficial asignada por DIAN:{' '}
          <strong>
            {DIAN_HABILITACION_SET.INVOICES_TARGET} facturas +{' '}
            {DIAN_HABILITACION_SET.DEBIT_NOTES_TARGET} notas débito +{' '}
            {DIAN_HABILITACION_SET.CREDIT_NOTES_TARGET} notas crédito
          </strong>{' '}
          transmitidas al ambiente de habilitación (vpfe-hab).
        </p>

        {/* Banner: transmisor DIAN bloqueado (cert ECD sin configurar) */}
        {!s.transmitter_ready && (
          <div className="mt-6 rounded-xl border border-warning/40 bg-warning-tint p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 flex-none text-warning-fg" />
              <div className="text-sm">
                <p className="font-semibold text-warning-fg">
                  Transmisión a DIAN bloqueada: certificado ECD pendiente
                </p>
                <p className="mt-1 text-fg-secondary">
                  El servidor aún no tiene el certificado de firma configurado
                  (típicamente falta el password del cert ECD) o el cliente SOAP
                  DIAN no está listo. Puedes crear los documentos del set en
                  borrador, pero <strong>ningún documento se puede transmitir</strong>{' '}
                  hasta resolverlo.
                </p>
                {s.transmitter_message && (
                  <p className="mt-2 rounded bg-bg-surface p-2 font-mono text-xs text-fg-muted">
                    {s.transmitter_message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Umbral real de aprobación */}
        {thresholdMet ? (
          <div className="mt-6 rounded-xl border-2 border-success/40 bg-success-tint p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 flex-none text-success-fg" />
              <div>
                <p className="text-lg font-bold text-success-fg">
                  Umbral cumplido: {s.invoices_accepted}{' '}
                  {s.invoices_accepted === 1 ? 'factura aceptada' : 'facturas aceptadas'} por DIAN
                </p>
                <p className="mt-1 text-sm text-fg-secondary">
                  Con 1 factura aceptada el set ya es aprobable. Completa la
                  transmisión de los documentos restantes y solicita el pase a
                  producción en{' '}
                  <a
                    href="https://muisca.dian.gov.co"
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-600 underline"
                  >
                    muisca.dian.gov.co
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-xl border-2 border-brand-600/40 bg-bg-surface p-5">
            <div className="flex items-start gap-3">
              <Target className="h-6 w-6 flex-none text-brand-600" />
              <div>
                <p className="text-lg font-bold">
                  Umbral real: 1 factura aceptada aprueba el set
                </p>
                <p className="mt-1 text-sm text-fg-secondary">
                  DIAN exige transmitir los {DIAN_HABILITACION_SET.INVOICES_TARGET} FE +{' '}
                  {DIAN_HABILITACION_SET.DEBIT_NOTES_TARGET} ND +{' '}
                  {DIAN_HABILITACION_SET.CREDIT_NOTES_TARGET} NC, pero basta{' '}
                  <strong>una sola factura ACEPTADA</strong> para que el set sea
                  aprobable — los demás documentos cuentan como transmitidos sin
                  importar el resultado.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progreso por tipo */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <TypeProgress
            title="Facturas (FE)"
            target={DIAN_HABILITACION_SET.INVOICES_TARGET}
            created={s.invoices_created}
            transmitted={s.invoices_transmitted}
            accepted={s.invoices_accepted}
            rejected={s.invoices_rejected}
          />
          <TypeProgress
            title="Notas débito (ND)"
            target={DIAN_HABILITACION_SET.DEBIT_NOTES_TARGET}
            created={ndDocs.length}
            transmitted={ndDocs.filter((d) => d.submitted_at).length}
            accepted={ndDocs.filter((d) => d.status === 'accepted').length}
            rejected={ndDocs.filter((d) => d.status === 'rejected').length}
          />
          <TypeProgress
            title="Notas crédito (NC)"
            target={DIAN_HABILITACION_SET.CREDIT_NOTES_TARGET}
            created={ncDocs.length}
            transmitted={ncDocs.filter((d) => d.submitted_at).length}
            accepted={ncDocs.filter((d) => d.status === 'accepted').length}
            rejected={ncDocs.filter((d) => d.status === 'rejected').length}
          />
        </div>

        {/* Acciones */}
        <section className="mt-8 rounded-xl border border-border bg-bg-surface p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            Acciones del set
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {!s.started && (
              <ActionButton
                onClick={() => createInvoices.mutate()}
                pending={createInvoices.isPending}
                pendingLabel="Creando lote..."
                icon={<PlayCircle className="h-4 w-4" />}
                label={`Crear lote inicial (${DIAN_HABILITACION_SET.HELPER_INVOICES} facturas)`}
              />
            )}
            {!s.credit_note_ready && (
              <ActionButton
                onClick={() => createNC.mutate()}
                pending={createNC.isPending}
                pendingLabel="Creando NC..."
                icon={<PlayCircle className="h-4 w-4" />}
                label="Crear nota crédito"
                // El backend referencia la 1ª factura ACEPTADA del set.
                disabled={s.invoices_accepted < 1}
                disabledHint="Requiere al menos 1 factura del set aceptada por DIAN"
              />
            )}
            {!s.debit_note_ready && (
              <ActionButton
                onClick={() => createND.mutate()}
                pending={createND.isPending}
                pendingLabel="Creando ND..."
                icon={<PlayCircle className="h-4 w-4" />}
                label="Crear nota débito"
                // El backend referencia la 2ª factura ACEPTADA del set.
                disabled={s.invoices_accepted < 2}
                disabledHint="Requiere al menos 2 facturas del set aceptadas por DIAN"
              />
            )}
            <ActionButton
              onClick={() => transmitPending.mutate(pending)}
              pending={transmitPending.isPending}
              pendingLabel={
                batch ? `Transmitiendo ${batch.done}/${batch.total}...` : 'Transmitiendo...'
              }
              icon={<Send className="h-4 w-4" />}
              label={`Transmitir pendientes (${pending.length})`}
              disabled={pending.length === 0 || !s.transmitter_ready || transmitting}
              disabledHint={
                !s.transmitter_ready
                  ? 'Transmisor DIAN bloqueado (cert ECD pendiente)'
                  : pending.length === 0
                    ? 'No hay documentos en borrador ni fallidos'
                    : undefined
              }
            />
          </div>
          <p className="mt-3 text-xs text-fg-muted">
            La transmisión usa SendTestSetAsync contra vpfe-hab: DIAN responde
            202 y el resultado definitivo llega por polling (esta página se
            actualiza cada {POLL_INTERVAL_MS / 1000}s mientras haya documentos
            en tránsito).
          </p>
        </section>

        {/* Tabla de documentos del set */}
        <section className="mt-6">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            Documentos del set ({docs.length})
          </h2>
          <div className="mt-2 overflow-x-auto rounded-xl border border-border bg-bg-surface">
            {docs.length === 0 ? (
              <div className="p-6 text-center text-sm text-fg-muted">
                El set no ha arrancado. Crea el lote inicial de facturas para comenzar.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-bg-elevated text-left text-xs uppercase tracking-wide text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">Número</th>
                    <th className="px-4 py-2.5">Tipo</th>
                    <th className="px-4 py-2.5">Estado DIAN</th>
                    <th className="px-4 py-2.5">Track ID</th>
                    <th className="px-4 py-2.5">CUFE</th>
                    <th className="px-4 py-2.5">Creada</th>
                    <th className="px-4 py-2.5">Transmitida</th>
                    <th className="px-4 py-2.5">Resultado</th>
                    <th className="px-4 py-2.5 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {docs.map((d) => {
                    const badge = STATUS_BADGES[d.status]
                    const rowSubmitting =
                      submitOne.isPending && submitOne.variables === d.id
                    return (
                      <tr key={d.id} className="hover:bg-bg-elevated">
                        <td className="px-4 py-2.5">
                          <Link
                            to={ROUTES.INVOICE_DETAIL.replace(':id', d.id)}
                            className="font-mono text-xs text-brand-600 hover:underline"
                          >
                            {d.doc_number}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-xs">{DOC_TYPE_LABELS[d.doc_type]}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full ${badge.bg} px-2.5 py-0.5 text-xs font-semibold ${badge.fg}`}
                            title={d.status_message ?? undefined}
                          >
                            {d.status === 'submitting' && (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            )}
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <CopyableCell value={d.track_id} label="Track ID" />
                        </td>
                        <td className="px-4 py-2.5">
                          <CopyableCell value={d.cufe} label="CUFE" />
                        </td>
                        <td className="px-4 py-2.5 text-xs text-fg-secondary">
                          {fmtDateTime(d.created_at)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-fg-secondary">
                          {fmtDateTime(d.submitted_at)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-fg-secondary">
                          {fmtDateTime(d.accepted_at ?? d.rejected_at)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {isPendingTransmit(d) ? (
                            <button
                              type="button"
                              onClick={() => submitOne.mutate(d.id)}
                              disabled={!s.transmitter_ready || transmitting}
                              title={
                                !s.transmitter_ready
                                  ? 'Transmisor DIAN bloqueado (cert ECD pendiente)'
                                  : undefined
                              }
                              className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface px-2.5 py-1 text-xs font-medium hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-bg-surface"
                            >
                              {rowSubmitting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                              {d.status === 'failed' ? 'Re-transmitir' : 'Transmitir'}
                            </button>
                          ) : (
                            <Link
                              to={ROUTES.INVOICE_DETAIL.replace(':id', d.id)}
                              className="inline-flex items-center gap-0.5 text-xs text-brand-600 hover:underline"
                            >
                              Ver <ChevronRight className="h-3 w-3" />
                            </Link>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {s.invoices_failed > 0 && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-warning-fg">
              <AlertTriangle className="h-3.5 w-3.5" />
              {s.invoices_failed}{' '}
              {s.invoices_failed === 1 ? 'factura falló' : 'facturas fallaron'} por error
              técnico — usa "Re-transmitir" (el consecutivo se conserva).
            </p>
          )}
        </section>

        <div className="mt-8 rounded-md border border-border bg-bg-elevated p-4 text-xs text-fg-secondary">
          <strong>Pre-requisitos:</strong> onboarding completo, al menos 1 cliente
          activo y 1 producto activo en el catálogo. El asistente automatiza los
          primeros documentos del set ({DIAN_HABILITACION_SET.HELPER_INVOICES} FE
          + 1 NC + 1 ND); las facturas y notas adicionales para completar la
          composición oficial se crean desde{' '}
          <Link to={ROUTES.INVOICES} className="text-brand-600 hover:underline">
            Facturas
          </Link>{' '}
          y se transmiten al mismo set de pruebas.
        </div>
      </div>
    </AppShell>
  )
}

// ─── Sub-componentes ─────────────────────────────────────────────────

function TypeProgress({
  title,
  target,
  created,
  transmitted,
  accepted,
  rejected,
}: {
  title: string
  target: number
  created: number
  transmitted: number
  accepted: number
  rejected: number
}) {
  const pct = (n: number) => Math.min(100, (n / target) * 100)
  return (
    <div className="rounded-xl border border-border bg-bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{title}</p>
        <p className="font-mono text-xs text-fg-muted">meta {target}</p>
      </div>
      {/* Barra apilada: aceptadas (verde) + rechazadas (rojo) + resto transmitido (info) */}
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-bg-elevated">
        <div className="flex h-full">
          <div className="h-full bg-success" style={{ width: `${pct(accepted)}%` }} />
          <div className="h-full bg-error" style={{ width: `${pct(rejected)}%` }} />
          <div
            className="h-full bg-info"
            style={{ width: `${pct(Math.max(0, transmitted - accepted - rejected))}%` }}
          />
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
        <ProgressStat label="Creados" value={created} />
        <ProgressStat label="Transmit." value={transmitted} />
        <ProgressStat label="Aceptados" value={accepted} ok={accepted > 0} />
        <ProgressStat label="Rechaz." value={rejected} danger={rejected > 0} />
      </dl>
    </div>
  )
}

function ProgressStat({
  label,
  value,
  ok,
  danger,
}: {
  label: string
  value: number
  ok?: boolean
  danger?: boolean
}) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</dt>
      <dd
        className={`mt-0.5 font-mono text-sm font-bold ${
          danger ? 'text-error-fg' : ok ? 'text-success-fg' : ''
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

function ActionButton({
  onClick,
  pending,
  pendingLabel,
  icon,
  label,
  disabled,
  disabledHint,
}: {
  onClick: () => void
  pending: boolean
  pendingLabel: string
  icon: React.ReactNode
  label: string
  disabled?: boolean
  disabledHint?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending || disabled}
      title={disabled ? disabledHint : undefined}
      className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {pending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" /> {pendingLabel}
        </>
      ) : (
        <>
          {icon} {label}
        </>
      )}
    </button>
  )
}

function CopyableCell({ value, label }: { value?: string; label: string }) {
  if (!value) return <span className="text-xs text-fg-muted">—</span>
  return (
    <span className="inline-flex items-center gap-1">
      <span className="max-w-[9rem] truncate font-mono text-xs text-fg-secondary" title={value}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => copyText(value, label)}
        title={`Copiar ${label}`}
        aria-label={`Copiar ${label}`}
        className="rounded p-0.5 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary"
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  )
}
